export const WEBSITE_CONTENT_MAP = `# Thruline Website Content Map
# Generated: 2026-02-22
# All routes and their visitor-facing content

routes:
  - path: /#/
    title: "Thruline | Custom ERP Built in 60 Days"
    description: "Stop fighting your off-the-shelf ERP. Thruline builds fully custom enterprise software for manufacturing and operations — fixed pricing, delivered fast."
    sections:
      - id: hero
        content: |
          # Custom ERP. Built in 60 days.

          Stop fighting your off-the-shelf ERP. Thruline builds fully custom enterprise software for manufacturing and operations — fixed pricing, delivered fast.

          **Now accepting new customers**

          Trusted across manufacturing, biotech, and logistics

      - id: social-proof
        content: |
          ## Trusted by innovative companies

          Hextronics, Octo Server, Crossbow Systems, FREDsense, Flight Deck Solutions, N Line Energy, Blissway, Flexxbotics, Aclarity

      - id: features
        content: |
          ## Why Thruline

          ### ERP that fits your business — not the other way around

          - **60-Day Delivery:** From kickoff to production in 60 days. We scope, build, and deploy your custom ERP on a fixed timeline — no surprises, no scope creep.

          - **Fully Customized:** Every workflow, dashboard, and integration is built around how your team actually works. No modules you don't need, no workarounds.

          - **Fixed Pricing:** Know exactly what you'll pay before we write a single line of code. Transparent pricing across three tiers — no hidden fees, ever.

      - id: cta
        content: |
          ## Get something built today

          Join manufacturing teams who replaced their legacy ERP with software that actually works for them.

  - path: /#/pricing
    title: "Pricing | Thruline"
    description: "Start where it matters most. Expand when you're ready. Every Thruline engagement starts with a free discovery phase and a fixed-price project."
    sections:
      - id: hero
        content: |
          # Start where it matters most. Expand when you're ready.

          Every Thruline engagement starts with a free discovery phase and a fixed-price project. Your team gets full platform access from day one — and when you're ready to expand, the next project works the same way.

      - id: how-it-works
        content: |
          ## How it works

          ### Every project follows three steps.

          Whether it's your first module or your fourth expansion, the process is the same: we scope it, we build it, you run it.

          **Step 1: Discovery — Free**

          You share your operational documents — SOPs, spreadsheets, emails, whatever captures how your team actually works. Thruline's system ingests them and reconstructs your workflows. A consultant reviews the output, identifies gaps, and builds a scoped project plan.

          What you walk away with:
          - A requirements map built from your own documents
          - Consultant's assessment: what works, what's broken, what the system should do
          - A scoped project plan with a fixed-price quote
          - No cost. No obligation. Walk away if it's not right.

          **Step 2: Implementation — Fixed price**

          You approve the project plan, and Thruline builds your system. Every configuration, workflow, and integration is traced back to a specific requirement from your documents. A consultant verifies the output. Documentation and training materials are generated automatically.

          What you walk away with:
          - A fully configured project — the module(s) you scoped, ready to operate
          - Every configuration traced to a documented requirement
          - Auto-generated documentation & training materials
          - Consultant-verified quality, not just automated output
          - Data migration from your existing systems

          **Step 3: Operate — Per user/month**

          Your project goes live. Every licensed user gets access to the full Thruline platform — not just the module you implemented, but everything. Reports, dashboards, workflows, document templates, all modules. No feature gating, no module-by-module upsells.

          What you walk away with:
          - Full platform access for every licensed user
          - All modules included, not just the one you implemented first
          - Unlimited workflows, reports, dashboards & document templates
          - Ongoing support from the team that built your system
          - A self-documenting ERP that trains your team as they use it

          Same process for every expansion. Discovery is always free.

      - id: expansion
        content: |
          ## Expand on your terms

          ### Your next project works the same way.

          You don't have to buy your entire ERP on day one. Start with the area that hurts the most, get it running, then expand when the business is ready.

          **Start with what matters most.**

          Most companies don't need their entire ERP on day one. You start with the area that hurts the most — maybe it's production, maybe it's inventory, maybe it's purchasing. You get that running. Your team learns the system in a real operational context. Then, when the next pain point surfaces or the business grows into a new area, you scope another project.

          **Each expansion follows the same three steps.**

          Free discovery, fixed-price project, same license. Your documents go in, a scoped plan comes out, you approve the price before work begins. Because your first project is already live, the system has context — the new project builds on top of what's already there, with the same traceability, the same documentation, and the same support team.

          **You're never locked in, and you're never locked out.**

          There's no penalty for starting small and no gate preventing you from expanding. Your license already includes every module. The only cost for an expansion is the project itself — scoped to exactly what you need, priced before you commit.

          ### Example expansion journey

          **Project 1 — Day one: Production + BoMs**
          Production tracking is manual and error-prone
          - Discovery: Free
          - Implementation: Fixed-price project
          - License: Per user / month

          **Project 2 — 6 months later: Inventory + Purchasing**
          Stockouts and over-ordering are costing money
          - Discovery: Free
          - Implementation: Fixed-price project
          - License: Per user / month (unchanged)

          **Project 3 — Year two: Field Operations**
          New service division launched
          - Discovery: Free
          - Implementation: Fixed-price project
          - License: New users added

      - id: platform-access
        content: |
          ## Full platform access

          ### One license. The full platform.

          Every user gets everything. No tiers. No upgrades. No modules to unlock.

          **Platform:**
          - All Thruline modules (Production, Inventory, Purchasing, BoMs, and more)
          - Unlimited triggered and scheduled workflows
          - Unlimited reports and dashboards
          - Unlimited document templates
          - Full mobile access

          **With every project:**
          - AI-powered document ingestion and requirements construction
          - Expert consultant review and gap analysis
          - Traced configuration (every setting linked to a requirement)
          - Auto-generated documentation and training materials
          - Data migration

          **Integrations:**
          - Pre-built integrations (QuickBooks, Shopify, Slack, Stripe, etc.)
          - Custom inbound webhooks
          - Single Sign-On (SSO)
          - Custom integrations (scoped during implementation)
          - GovCloud available for government contractors

          **Support:**
          - Ongoing support from the team that built your system
          - Workflow development assistance
          - System evolution as your operations change
          - Self-documenting ERP — always-current training materials

      - id: philosophy
        content: |
          ## Our philosophy

          ### The logic behind the pricing.

          **"Why is discovery free?"**

          Because we're not interviewing your team for six weeks. Thruline reads your documents and reconstructs your workflows in hours, not months. The discovery phase produces a real, scoped project plan with a fixed-price quote. If it doesn't make sense for you, you've lost nothing. We can do this because the Thruline ERP Factory makes discovery dramatically cheaper than traditional consulting — so we absorb the cost.

          **"Why fixed-price projects instead of hourly billing?"**

          Because hourly billing is how ERP implementations balloon. The consultant's meter starts running in the first discovery meeting and doesn't stop until months after go-live. Thruline scopes each project during discovery and quotes a number you approve before work begins. The Thruline ERP Factory automates the production work and focuses consultant time on expert review. The result: predictable cost, no overruns, and you know exactly what you're paying for.

          **"Why pay per project instead of one big upfront fee?"**

          Because most companies don't need their entire ERP on day one, and paying for everything upfront means paying for modules you won't use for months or years. The per-project model lets you start where the pain is, prove value with your team, and expand when the business is ready — not when a sales contract forces you to. Each project is a self-contained engagement with its own scope, its own price, and its own deliverable. You're never overpaying for capabilities you haven't activated.

          **"Why one license tier instead of Starter, Pro, and Enterprise?"**

          Because feature gating is how legacy ERP vendors extract revenue after the sale. You buy the base tier, realize you need something locked behind the next one, and upgrade under pressure. Thruline includes everything in every license. The project determines which modules your team uses first; the license gives them access to all of them. When you're ready to activate a new module, you run a project — not an upgrade purchase.

      - id: comparison
        content: |
          ## Comparison

          ### How the old model compares.

          | | Traditional ERP | Thruline |
          |---|---|---|
          | Discovery cost | Paid from meeting one | Free |
          | Pricing model | Hourly, open-ended | Fixed price per project, scoped upfront |
          | Scope clarity | Emerges over months | Defined before you commit |
          | Entry commitment | Buy the whole system upfront | Start with one project, expand later |
          | Feature access | Tiered / per-module licensing | Everything included in every license |
          | Timeline | 3–9 months | Weeks per project |
          | Documentation | Goes stale before go-live | Auto-generated, traced, always current |
          | Support model | Generic helpdesk | The team that built your system |
          | Traceability | None | Every config traced to a requirement |
          | Expansion cost | Module upgrade fees + re-implementation | New project, same process, fixed price |

      - id: cta
        content: |
          ## Start with what matters most. Discovery is free.

          Send us your documents. We'll send you a project plan and a fixed-price quote. No cost, no commitment.

  - path: /#/solutions
    title: "Solutions | Thruline"
    description: "Operational modules built to work together. Each one is configured to match how your business actually runs — not how a generic system thinks it should."
    sections:
      - id: hero
        content: |
          # Modules and industries

          Operational modules built to work together. Each one is configured to match how your business actually runs — not how a generic system thinks it should.

      - id: modules
        content: |
          ## Operational modules

          **Purchasing**
          From requisition to receipt — streamline your entire procurement process so you get the right materials, from the right vendors, at the right time.

          **Inventory Management**
          Know what you have, where it is, and when to reorder. Real-time visibility across every warehouse, bin, and lot.

          **Production**
          Real-time visibility from order release to shipment. Track production, capture data at every operation, and make decisions based on what's actually happening.

          **Quality**
          From incoming inspection to final shipment — catch defects early, close CAPAs fast, and maintain compliance without the paperwork overhead.

          **Sales & Quoting**
          Build quotes from real cost data, convert to orders in one click, and track every deal from first inquiry to final invoice.

          **Custom Operating System**
          Your business is unique. Thruline builds software that adapts to you — every workflow, dashboard, and integration designed around how your team actually works.

      - id: industries
        content: |
          ## Industries we serve

          **Manufacturing**
          From job shops to high-volume production, Thruline gives manufacturing teams the tools to plan, execute, and improve — without fighting their software.

          **Industrial Operations**
          Whether you're managing field operations, facility maintenance, or multi-site logistics, Thruline builds the operational backbone your team needs.

          **Complex Consumer Products**
          Consumer products companies deal with variants, regulations, and multi-channel complexity. Thruline builds the operational system that keeps it all connected.

          **Biotech**
          From lab bench to commercial manufacturing, Thruline connects your LIMS, ELN, and operational data into a single system — so your team can focus on science, not spreadsheets.

      - id: cta
        content: |
          ## Not sure where to start?

          Discovery is free. Share your documents and we'll build a project plan that shows you exactly which modules fit your operations.

  - path: /#/solutions/quality
    title: "Quality Module | Thruline"
    description: "From incoming inspection to final shipment, Thruline's quality module gives your team the tools to catch defects early, close CAPAs fast, and maintain compliance without the paperwork overhead."
    sections:
      - id: hero
        content: |
          # Quality management that keeps pace with production

          From incoming inspection to final shipment, Thruline's quality module gives your team the tools to catch defects early, close CAPAs fast, and maintain compliance without the paperwork overhead.

      - id: features
        content: |
          ## Features

          **Incoming Inspection**
          Define inspection plans for raw materials and purchased parts. Automate sampling rules, record pass/fail results, and quarantine non-conforming lots before they reach production.

          **Non-Conformance Reports (NCR)**
          Capture and track quality deviations across receiving, in-process, and final inspection. Route NCRs through review workflows with full disposition tracking.

          **Audit Management**
          Schedule and conduct internal, supplier, and customer audits. Track findings, assign corrective actions, and maintain audit history.

          **Certificate of Conformance**
          Auto-generate Certificates of Conformance from inspection data and test results. Attach material certs, test reports, and compliance documentation.

          **Calibration Management**
          Track gage and instrument calibration schedules, certificates, and due dates. Receive automated reminders and flag overdue equipment.

          **Lot & Batch Traceability**
          Trace materials from receiving through production to shipment. Link lot numbers, serial numbers, and inspection records for full traceability.

      - id: faqs
        content: |
          ## Frequently Asked Questions

          **Can we integrate quality data with our existing production system?**
          Yes. Quality checkpoints can be embedded directly into production workflows, so inspection data is captured as part of the manufacturing process — not as a separate step. If you have existing quality data in spreadsheets or another system, we migrate it during implementation.

          **How does traceability work in practice?**
          Every lot and serial number is tracked through every transaction — receiving, production, inspection, and shipment. If a quality issue arises, you can trace forward (where did this material go?) and backward (where did this material come from?) in seconds. This is critical for recalls, customer complaints, and audit responses.

          **Do we need separate software for calibration management?**
          No. Calibration management is built into the quality module. You can track every gage and instrument, manage calibration schedules, store certificates, and get automated reminders — all within the same system your inspectors use for incoming and in-process inspection.

          **Can we customize inspection forms and checklists?**
          Absolutely. Every inspection plan, checklist, and form is configured to match your specific requirements during implementation. You define the characteristics, measurement methods, acceptance criteria, and sampling rules. As your products or processes change, we update the forms to match.

          **How long does it take to implement the quality module?**
          Implementation timelines depend on the complexity of your quality requirements, but most quality module implementations are completed within the standard Thruline delivery window. Discovery is free and gives you a detailed implementation plan with a fixed-price quote before you commit.

  - path: /#/solutions/manufacturing-mes
    title: "Production Module | Thruline"
    description: "Thruline's production module connects your shop floor to your front office. Track production, capture data at every operation, and make decisions based on what's actually happening — not what was planned."
    sections:
      - id: hero
        content: |
          # Real-time visibility from order release to shipment

          Thruline's production module connects your shop floor to your front office. Track production, capture data at every operation, and make decisions based on what's actually happening — not what was planned.

      - id: features
        content: |
          ## Features

          **Work Order Management**
          Create, schedule, and track work orders from release through completion. Manage priorities, dependencies, and split operations across work centers.

          **Bill of Materials**
          Define multi-level BOMs with revision control, effectivity dates, and engineering change management. Support phantom assemblies, substitutes, and configurable products.

          **Routing & Operations**
          Define step-by-step manufacturing routings with operation times, work center assignments, and tooling requirements. Track actual vs. planned cycle times.

          **Production Scheduling**
          Finite and infinite capacity scheduling with drag-and-drop Gantt charts. Balance load across work centers and optimize sequence for minimum changeover.

          **Rework & Scrap Management**
          Manage rework orders, track scrap by reason code, and link to non-conformance reports. Calculate true cost of quality at the operation level.

          **Labor Tracking**
          Record direct and indirect labor by employee, work order, and operation. Support clock-in/out, crew assignments, and labor efficiency reporting.

      - id: faqs
        content: |
          ## Frequently Asked Questions

          **How do operators interact with the system on the shop floor?**
          Operators use touchscreen terminals, tablets, or barcode scanners at their workstations. The interface is designed for shop floor conditions — large buttons, clear visuals, minimal typing. Operators clock into operations, record output, log material usage, and flag issues without leaving their station.

          **Can the production module handle both job shop and repetitive manufacturing?**
          Yes. Thruline is configured to match your specific manufacturing model — whether that's high-mix low-volume job shop work, repetitive production runs, or a hybrid. Work order management, scheduling, and routing are all adapted to how your shop actually operates.

          **How does production scheduling handle rush orders?**
          Rush orders can be flagged and prioritized in the scheduling system. The Gantt-based scheduler shows the impact of inserting a rush order on existing commitments, so planners can make informed decisions about trade-offs. What-if scenarios let you test options before committing.

          **How is rework tracked differently from original production?**
          Rework orders are created as separate work orders with their own routing and material requirements. They're linked to the original work order and the non-conformance report that triggered them. This gives you accurate cost tracking for rework and clear visibility into cost of poor quality.

          **Can we track labor costs by job?**
          Yes. When operators clock into work orders and operations, their labor time is allocated directly to that job. The system calculates labor cost using your configured rates and compares actual labor to standard hours. This gives you accurate job costing and labor efficiency metrics.

  - path: /#/solutions/inventory-management
    title: "Inventory Management | Thruline"
    description: "Thruline's inventory module gives you real-time visibility across every warehouse, bin, and lot — so you can fill orders faster, reduce carrying costs, and never run out of critical materials."
    sections:
      - id: hero
        content: |
          # Know what you have, where it is, and when to reorder

          Thruline's inventory module gives you real-time visibility across every warehouse, bin, and lot — so you can fill orders faster, reduce carrying costs, and never run out of critical materials.

      - id: features
        content: |
          ## Features

          **Multi-Location Inventory**
          Track stock across warehouses, bins, and zones. Manage transfers between locations with full audit trail and real-time availability.

          **Barcode Scanning**
          Receive, pick, and ship with barcode scanning. Eliminate manual entry errors and speed up warehouse operations.

          **Lot & Serial Tracking**
          Track inventory by lot number, serial number, or batch. Support FIFO, FEFO, and custom allocation rules for regulated industries.

          **Cycle Counting**
          Schedule and execute cycle counts by location, ABC classification, or item group. Reconcile variances and maintain inventory accuracy.

          **Reorder Points**
          Set min/max levels, safety stock, and reorder points. Generate purchase requisitions automatically when stock falls below threshold.

          **Receiving & Putaway**
          Receive against purchase orders with quantity and quality checks. System-directed putaway suggests optimal bin locations.

      - id: faqs
        content: |
          ## Frequently Asked Questions

          **Can we manage inventory across multiple warehouses?**
          Yes. Thruline supports unlimited warehouse locations, each with its own zone, aisle, rack, and bin structure. You get consolidated visibility across all locations while warehouse managers can focus on their specific facility. Inter-warehouse transfers are tracked with full in-transit visibility.

          **What barcode scanning hardware do we need?**
          Thruline works with standard barcode scanners and mobile devices, including smartphones. Most customers use handheld Bluetooth scanners paired with tablets or smartphones, or dedicated warehouse scanning devices. We support 1D barcodes and 2D codes (QR, Data Matrix). The system prints labels directly, so you don't need separate labeling software.

          **How does the system handle inventory accuracy?**
          Cycle counting replaces disruptive annual physical inventories. You schedule counts by ABC classification, location, or item group. The system generates count sheets, highlights variances, and routes significant adjustments through approval workflows. Inventory accuracy metrics are tracked over time so you can see improvement.

          **Can reorder points be set automatically?**
          Yes. You can set reorder parameters manually or let the system suggest optimal levels based on historical demand patterns, lead times, and your desired service level. When stock drops below the reorder point, purchase requisitions are generated automatically and routed through your approval workflow.

          **Does the inventory module integrate with purchasing and production?**
          Fully. Material receipts from purchasing flow directly into inventory. Production material issues deduct from stock in real time. Reorder points trigger purchase requisitions automatically. It's one connected system — no manual reconciliation between modules.

          **How do you handle lot traceability for regulated industries?**
          Every lot and serial number is tracked through every transaction from receiving to shipment. You can configure FIFO, FEFO, or custom allocation rules. Lot attributes like expiration dates, supplier lot numbers, and test results are stored and available for compliance reporting.

  - path: /#/solutions/sales-and-quoting
    title: "Sales & Quoting | Thruline"
    description: "Build quotes from real cost data, convert to orders in one click, and track every deal from first inquiry to final invoice. No spreadsheets, no re-keying, no guesswork."
    sections:
      - id: hero
        content: |
          # From quote to cash — faster and more accurate

          Build quotes from real cost data, convert to orders in one click, and track every deal from first inquiry to final invoice. No spreadsheets, no re-keying, no guesswork.

      - id: features
        content: |
          ## Features

          **Quote Builder**
          Build detailed quotes with line items, quantities, pricing tiers, and optional configurations. Clone and revise quotes without losing history.

          **Approval Workflows**
          Route quotes through approval chains based on value, margin, or customer tier. Track who approved what and when.

          **Customer Management**
          Maintain customer records with contacts, ship-to addresses, payment terms, and pricing agreements.

          **Price Lists & Discounts**
          Manage customer-specific pricing, volume discounts, and contract pricing. Apply discount rules automatically.

          **Sales Order Processing**
          Manage the full order lifecycle from entry through invoicing. Track order status, backorders, partial shipments, and returns.

          **Sales Analytics**
          Analyze win rates, quote-to-order conversion, revenue by customer, and pipeline value.

      - id: faqs
        content: |
          ## Frequently Asked Questions

          **Can quotes pull pricing from our existing price lists automatically?**
          Yes. The quote builder pulls from your configured price lists, customer-specific pricing agreements, and volume discount rules. When you add a line item, the system applies the best available price automatically. You can override prices manually when needed, with approval workflows for below-threshold margins.

          **How does quote-to-order conversion work?**
          When a customer accepts a quote, you convert it to a sales order with one click. All line items, pricing, terms, and delivery dates carry forward automatically. The conversion can trigger downstream processes — inventory allocation, production planning, and purchasing — without any re-entry.

          **Can we track why we win or lose deals?**
          Yes. Sales analytics track win rates, quote-to-order conversion, and revenue by customer, product, and region. When a quote is marked as lost, you can record the reason. Over time, this data reveals patterns — pricing too high, lead times too long, or specific competitors winning certain types of work.

          **Does the module integrate with our accounting system?**
          Thruline integrates with popular accounting platforms like QuickBooks and Xero. Sales orders and invoices can sync automatically, eliminating duplicate data entry. The specific integration is configured during implementation based on your accounting workflow.

          **Can multiple people work on quotes simultaneously?**
          Quotes are assigned to owners, and the system tracks who's working on what. Approval workflows ensure the right people review quotes before they go to customers. Revision management preserves every version, so there's no confusion about which version was sent or approved.

          **How do approval workflows handle different quote types?**
          Approval rules are fully configurable. You can route quotes based on total value, margin percentage, customer tier, product type, or any combination. Multi-level approval chains support both sequential and parallel review. Approvers get notifications and can act from email or the system.

  - path: /#/solutions/purchasing
    title: "Purchasing | Thruline"
    description: "From requisition to receipt, Thruline's purchasing module streamlines your entire procurement process — so you get the right materials, from the right vendors, at the right time."
    sections:
      - id: hero
        content: |
          # Procurement that keeps your production line fed

          From requisition to receipt, Thruline's purchasing module streamlines your entire procurement process — so you get the right materials, from the right vendors, at the right time.

      - id: features
        content: |
          ## Features

          **Purchase Requisitions**
          Generate requisitions manually or automatically from MRP, reorder points, or production demand. Route through approval workflows before converting to POs.

          **Purchase Order Management**
          Create, send, and track purchase orders with line-level detail. Manage blanket orders, scheduled releases, and change orders.

          **Purchase Requests (RFQs)**
          Send RFQs to multiple vendors, compare responses side-by-side, and award based on price, lead time, and quality score.

          **Receiving & 3-Way Match**
          Receive against POs with quantity and quality verification. Automatically match PO, receipt, and invoice for streamlined AP processing.

          **Vendor Management**
          Maintain approved vendor lists with certifications, lead times, and performance history. Track vendor scorecards.

          **Contract & Pricing Management**
          Negotiate and store vendor contracts with pricing tiers, volume discounts, and effective dates.

      - id: faqs
        content: |
          ## Frequently Asked Questions

          **Can purchase orders be generated automatically?**
          Yes. Purchase requisitions can be generated automatically from reorder points, MRP planned orders, or production shortage alerts. Once approved, requisitions convert to purchase orders with vendor, pricing, and delivery information pre-populated from your master data.

          **How does three-way matching work?**
          The system automatically compares three documents: the purchase order (what you ordered), the receiving record (what you received), and the vendor invoice (what you're being billed for). If all three match within your configured tolerance, the invoice is approved for payment automatically. Discrepancies are flagged for review.

          **Can we track vendor performance over time?**
          Yes. Vendor scorecards are calculated automatically from your transactional data — on-time delivery percentage, quality acceptance rate, price competitiveness, and responsiveness. Scores update in real time and can trigger actions like corrective action requests or re-qualification audits when a vendor drops below threshold.

          **Does the system support blanket purchase orders?**
          Yes. Blanket orders establish a commitment to purchase a total quantity over a defined period. Releases are scheduled as needed — manually or automatically from MRP. The system tracks utilization and alerts you when a blanket is approaching expiration with unused quantities.

          **How do you handle vendor onboarding?**
          Vendor qualification workflows manage the full onboarding process — from initial evaluation through approval. Track certifications, capabilities, insurance, and qualification status. Only approved vendors appear as options when creating purchase orders for their qualified commodities.

          **Can we send POs to vendors electronically?**
          Yes. Purchase orders can be sent to vendors via email directly from the system, with your branded PO template attached as a PDF. The system tracks whether the vendor has acknowledged the PO and monitors delivery status against promised dates.

  - path: /#/solutions/custom
    title: "Thruline Core | Thruline"
    description: "A custom-developed ERP without needing developers. Thruline helps you plan and build it. You own it, understand every workflow and rule, and evolve it yourself as your business grows."
    sections:
      - id: hero
        content: |
          # A custom-developed ERP. No developers needed.

          Thruline helps you plan and build a system configured to your actual operations. You own it, understand every workflow and rule, and evolve it yourself as your business grows — with AI assistance and expert advisory when you need it.

      - id: the-problem
        content: |
          ## The Problem

          ### You know your business. Your ERP doesn't.

          **1. Off-the-shelf ERP forces you to work the way the software was designed**, not the way your operation actually runs. You end up with workarounds, shadow spreadsheets, and processes that exist only because the system can't handle how you actually do things.

          **2. Hiring a team to build something custom solves the fit problem** — but requires developers, implementation consultants, and ongoing technical staff that most manufacturers can't justify. The cost of having your own system has always been the cost of having your own team.

          **3. Handing it to a managed service provider gets you a custom system** — but you trade one dependency for another. Every change goes through their queue. Every modification is on their timeline. You use the system, but you never truly own it.

          **Thruline Core gives you the system without the team — and the control without the dependency.**

      - id: what-is-core
        content: |
          ## What Is Thruline Core

          ### Your own operating system. Built together. Owned by you.

          **Custom-Developed to Your Operation**
          Core isn't a template you configure — it's a platform where your actual workflows, rules, and processes become the system. Production scheduling, inventory, purchasing, quality, shipping — all built around how your operation actually works, not how a vendor thinks it should.

          **Thruline Is Your Development Team**
          You don't start with a blank screen. Thruline's AI reads your existing documents to map your operation, then our team builds the first version with you — walking you through every workflow, every rule, and why it was configured that way. The goal isn't just a working system. It's a working system you understand.

          **Full Transparency. Full Ownership.**
          Every workflow, rule, automation, and connection in your system is visible. Nothing is hidden in proprietary code or managed behind a curtain. You can open any process and see exactly how it works, what triggers it, and what documents informed its design. You can't evolve what you can't see — so Core shows you everything.

          **Evolve It Yourself**
          When your business changes, you change the system — directly. Describe what you need in plain language and AI suggests the configuration changes. See exactly what each modification affects before it goes live. Flag anything for Thruline expert review when the stakes are high. No tickets. No waiting. No change orders. You hold the wrench.

      - id: who-its-for
        content: |
          ## Who It's For

          ### Built for manufacturers who want to own their system — not just use it.

          Thruline Core is for you if:
          - You're a manufacturer with 50–500 employees running operations that have outgrown spreadsheets and basic software
          - You have no development team, or a very small one that's already stretched thin
          - You've been burned by off-the-shelf ERP that didn't fit, or custom ERP you couldn't maintain
          - You want to understand and modify your system without calling a vendor every time something changes
          - You want the kind of system that only companies with full dev teams and internal consultants can typically build — without hiring them

      - id: how-it-works
        content: |
          ## How It Works

          ### Thruline helps you plan and build it. You own it from there.

          **Phase 1: Discovery — Free**
          Thruline's AI reads your existing documents — SOPs, spreadsheets, emails, PDFs — and maps your actual operational processes. Our team reviews the results and builds a configuration blueprint: what your system needs, how it should work, and why.
          What you get: A clear picture of your operation translated into system architecture. No guesswork. No months of consultant interviews.

          **Phase 2: First Build — Fixed Quote**
          Thruline builds the initial system with you using the discovery blueprint. Every configuration is transparent, documented, and explained. We walk you through what was built and why, tying each decision back to the documents and processes that informed it. You're not just getting a system — you're learning how it works.
          What you get: A working system configured to your operation, on a platform you fully understand and can modify. Not a black box.

          **Phase 3: Operate & Evolve — Platform License**
          You run your business on Core. When something changes — new product line, new process, new requirement — you describe the change in plain language and the AI suggests configuration modifications. You see the impact before anything goes live. Flag changes for Thruline expert review when you want a second opinion. You have day-to-day control. Thruline advisory support is there when you need it.
          What you get: A system that evolves with your business — on your timeline, not a vendor's. Ongoing control without ongoing dependency.

      - id: what-makes-core-different
        content: |
          ## What Makes Core Different

          ### The capabilities that replace your development team.

          **Full Configuration Transparency**
          Every workflow, rule, automation, and connection is visible. Open any process and see exactly how it works, what triggers it, and what documents informed its design. The system is an open book.

          **AI-Assisted Self-Configuration**
          Describe changes in plain operational language. The AI suggests configuration modifications based on your existing setup and discovery data. Review, adjust, deploy — no programming required.

          **Change Impact Visibility**
          Before any modification goes live, see exactly what it affects downstream. "Changing this receiving workflow impacts these 3 processes and these 2 reports." No surprises.

          **Self-Documenting System**
          Every configuration traces to its source document or decision. When someone asks "why does this work this way?" the system shows them. Knowledge stays in the system, not in people's heads.

          **On-Demand Expert Review**
          Flag any modification for Thruline consultant review before deploying. Build it yourself, get an expert eye when the stakes are high. Like a pull request — optional, not required.

          **Your Brand, Your System**
          Core runs under your company's identity. Your team logs into your system, not a vendor's platform. When your warehouse team sees "YourCompany Operations," they own it.

          **Full Module Suite**
          Production scheduling, inventory management, purchasing, receiving, shipping, quality management, field operations, maintenance tracking. Start with what you need most.

      - id: faqs
        content: |
          ## Common Questions

          ### Why Core

          **"Why not just buy off-the-shelf ERP?"**
          Because off-the-shelf systems make you work their way. You end up with workarounds, shadow spreadsheets, and processes that exist because the software can't handle how you actually operate. Core is custom-developed around your operation from day one, and you can adjust it when your operation changes — the same day, without a vendor.

          **"Why not hire a team to build something custom?"**
          Because that requires developers, implementation consultants, and ongoing technical staff that most manufacturers can't justify. Core gives you the result — a system built around your business — without the overhead. Thruline is the development team you don't have to hire.

          **"Why Core instead of a managed service like Thruline Enterprise?"**
          Core is for companies that want to own and evolve their system — not just use it. You get full transparency into every configuration, AI tools to make changes yourself, and the freedom to adjust things on your timeline. Enterprise is great if you want Thruline to handle everything. Core is for when you want the wrench in your own hand.

          **"What if I break something?"**
          Core shows you the impact of every change before you deploy it. You can see what a modification affects downstream. You can flag any change for Thruline review before it goes live. And every Core customer gets a complete sandbox environment — test any change in full isolation before it touches your live system.

      - id: comparison
        content: |
          ## Comparison

          ### Thruline Core vs. Traditional ERP

          | | Traditional ERP | Thruline Core |
          |---|---|---|
          | How it's built | Vendor configures to their templates | Custom-developed around your actual processes |
          | Who understands it | The vendor (or consultants) | Your team — full transparency into every rule |
          | When you need a change | File a ticket. Wait. Pay. | Describe it. See the impact. Deploy it. |
          | Documentation | Binder from implementation day | Self-documenting. Always current. |
          | Your brand | Vendor's interface with your data | Your system, your identity |
          | Ongoing cost model | License + change orders + consulting | Platform license + advisory as needed |
          | Time to first change | Weeks to months | Same day |
          | Knowledge retention | Walks out the door with employees | Built into the system permanently |
          | Who it requires | IT dept or vendor dependency | Your operations team + Thruline advisory |

      - id: two-ways
        content: |
          ## Two Ways to Work With Thruline

          ### Want to own it, or want it done?

          Both start with the same AI-powered Discovery. The difference is who holds the wrench after go-live.

          **Thruline Core (You're here)**
          A custom-developed ERP you own and evolve. Thruline helps you plan and build it. You understand it, modify it, and grow it.
          - Thruline builds the first version with you
          - You operate with full transparency and AI assistance
          - You make changes directly, on your timeline
          - Best for: companies that want to own it

          **Thruline Enterprise**
          A custom-configured ERP, built and maintained by Thruline. We handle the system. You handle the business.
          - Thruline builds the entire system
          - Thruline operates and maintains as managed service
          - Changes handled by Thruline's team
          - Best for: companies that want it done

          Some customers start with Core and bring in Thruline for Enterprise-style builds on major expansions. Others start with Enterprise and transition to Core as their team builds confidence. The two products complement each other.

      - id: cta
        content: |
          ## Your operation is ready. Let's build the system it deserves.

          Discovery is free. We'll read your documents, map your processes, and show you what Core looks like for your business.

  - path: /#/solutions/enterprise
    title: "Thruline Enterprise | Thruline"
    description: "A fully custom ERP, configured to your actual operations and maintained by Thruline's team. We handle the system. You handle the business."
    sections:
      - id: hero
        content: |
          # Your custom ERP. Built and run by experts.

          Thruline Enterprise is a fully custom ERP, configured to your actual operations and maintained by Thruline's team. We handle the system. You handle the business.

      - id: the-promise
        content: |
          ## The Promise

          ### A system built around your operation. Not the other way around.

          **Your business has its own way of working.** Your receiving process, your production scheduling, your quality checks — they exist because they work for your operation. An ERP should reflect that reality, not replace it with someone else's template.

          **But building a custom system takes a team most manufacturers don't have.** Enterprise architects, implementation consultants, ongoing developers — the cost and complexity of standing up that capability internally puts truly custom ERP out of reach.

          **Thruline Enterprise is that team.** We discover how your operation actually works, build a system that matches, and keep it running and evolving as your business grows. You focus on your business. We focus on your system.

      - id: how-it-works
        content: |
          ## How It Works

          ### We discover it. We build it. We run it.

          **Phase 1: Discovery — Free**
          Thruline's AI reads your existing documents — SOPs, spreadsheets, emails, PDFs — and maps your actual operational processes. Our team reviews the results and designs the system architecture: what your ERP needs to do, how the modules connect, and why.
          What you get: A complete picture of your operation translated into system architecture — without months of consultant interviews.

          **Phase 2: Implementation — Fixed Quote**
          Thruline builds your complete system. We handle architecture decisions, workflow design, integrations, testing, and deployment. You participate in review checkpoints to validate that the system matches your operation — but the build is on us.
          What you get: A production-ready ERP configured to your actual processes, delivered at a fixed price scoped after Discovery.

          **Phase 3: Operate — Managed Service**
          You use your ERP to run your business. Thruline manages everything behind the scenes: updates, system health, change requests, and ongoing optimization. When your business changes, you tell us what's different — we adjust the system to match.
          What you get: A system that stays aligned with your operation without requiring internal technical staff to maintain it.

      - id: who-its-for
        content: |
          ## Who It's For

          ### For companies that want a custom ERP without the custom ERP headache.

          Thruline Enterprise is for you if:
          - You want a fully custom ERP without any involvement in the technical build
          - You prefer to hand the system to an expert team and focus entirely on running your business
          - You have the budget for a managed service and value speed over hands-on control
          - You need complex integrations, compliance requirements, or multi-site deployments that benefit from dedicated expert management
          - You don't want to invest time learning system configuration — you want it done

      - id: whats-included
        content: |
          ## What's Included

          ### Everything built, managed, and maintained.

          **Full Module Suite**
          Production, inventory, purchasing, shipping, quality, field operations — every module your operation needs, configured and integrated by Thruline.

          **Thruline-Built Configuration**
          Every workflow, rule, and automation is designed and built by Thruline's team based on your actual operational processes. No templates. No compromises.

          **Managed Operations**
          System updates, performance monitoring, change requests, and ongoing optimization — all handled by Thruline. Your team uses the system; we keep it running.

          **Continuous Alignment**
          When your business changes, the system changes with it. New product lines, new processes, new requirements — tell Thruline, and we adjust.

          **Integrations & Compliance**
          Thruline handles integrations with your existing tools, compliance requirements, and multi-site coordination. Complex technical work stays off your plate.

          **Full Documentation**
          Your system is fully documented and traceable. Every configuration decision links back to the operational process that informed it. Maintained by Thruline.

          **Expert Quality Control**
          Every change goes through Thruline's review process. Architecture decisions, workflow modifications, and system updates are validated by experts before deployment.

          **Expansion on Demand**
          Ready for new modules or major system expansion? Same process — Discovery, fixed quote, Thruline builds it. Grows with your business without growing your team.

          **Fast Delivery**
          Thruline's AI-powered discovery and implementation process delivers production-ready systems in weeks, not the months or years typical of traditional ERP.

      - id: faqs
        content: |
          ## Common Questions

          ### Why Thruline Enterprise

          **"How is this different from hiring an ERP consultant?"**
          Traditional consultants interview your team for months, configure a system based on their interpretation, and hand you the keys. Thruline's AI reads your actual documents to understand your operation — no interpretation gap. Then we stay. We don't hand you the keys and leave. We operate and maintain the system as a managed service.

          **"What if our business changes after go-live?"**
          That's expected. When your operation changes — new product line, new process, new supplier — you tell Thruline what's different. We scope the change, quote it if it's significant, and implement it. Standard adjustments are included in your managed service. Your system stays aligned with your business.

          **"How long does implementation take?"**
          Thruline's AI-powered discovery and build process is dramatically faster than traditional ERP implementation. Discovery takes days, not months. The full build depends on scope, but most initial implementations are measured in weeks. You'll get a specific timeline after Discovery.

          **"What if we want more control later?"**
          Thruline also offers Core — a self-evolving platform where your team takes on day-to-day configuration with Thruline advisory support. Enterprise customers can transition to Core if they develop the internal capability and desire for more hands-on control. The two products complement each other.

      - id: comparison
        content: |
          ## Comparison

          ### Enterprise vs. Traditional ERP

          | | Traditional ERP | Thruline Enterprise |
          |---|---|---|
          | Discovery method | Months of consultant interviews | AI reads your documents in days |
          | How it's configured | Vendor templates with workarounds | Built around your actual processes |
          | Implementation time | 6–18 months typical | Weeks, not months |
          | Pricing model | Unpredictable change orders | Fixed quote after Discovery |
          | After go-live | Consultant leaves, knowledge goes with them | Thruline stays as managed service |
          | When you need changes | Re-engage consultants, new SOW | Tell Thruline, we handle it |
          | Documentation | Delivered once, immediately outdated | Self-documenting, always current |
          | Internal team required | IT staff or vendor dependency | None — Thruline is your team |
          | System evolution | Expensive upgrade cycles | Continuous alignment with your operation |

      - id: two-ways
        content: |
          ## Two Ways to Work With Thruline

          ### Want it done, or want to own it?

          Both start with the same AI-powered Discovery. The difference is who holds the wrench after go-live.

          **Thruline Enterprise (You're here)**
          A fully custom ERP built and maintained by Thruline. We handle the system. You handle the business.
          - Thruline builds the entire system
          - Thruline operates and maintains
          - Changes handled by Thruline's team
          - Best for: companies that want it done

          **Thruline Core**
          A custom-developed ERP you own and evolve. Thruline helps you plan and build it. You understand it, modify it, and grow it.
          - Thruline builds the first version with you
          - You operate with Thruline advisory
          - You make changes with AI assistance
          - Best for: companies that want to own it

          Some customers start with Enterprise and transition to Core as their team builds confidence. Others start with Core and bring in Enterprise for major expansions. The two products complement each other.

      - id: cta
        content: |
          ## Your business already knows how it works. Let us read it.

          Discovery is free. We'll read your documents, map your processes, and show you what your custom ERP looks like — before you commit to anything.

  - path: /#/solutions/receivepro
    title: "ReceivePro | Thruline"
    description: "Your dock team should be moving pallets, not typing into an ERP. ReceivePro turns a photo of a packing slip into a completed receiving record."
    sections:
      - id: hero
        content: |
          # Your dock team should be moving pallets, not typing into an ERP

          ReceivePro turns a photo of a packing slip into a completed receiving record. An AI agent handles the matching, the receiving, and the ERP sync — so your team can scan and walk away.

          Works with Thruline, NetSuite, Epicor, SAP, and more — no ERP switch required.

      - id: how-it-works
        content: |
          ## How It Works
          Scan the packing slip. Walk to the next box.

          **01 Scan** (User — 10 seconds)
          Your dock worker photographs the bill of lading or packing slip with their phone. That's their only job.

          **02 Extract** (Agent — automatic)
          AI reads the document and pulls out the PO number, line items, quantities, units of measure, and tracking number.

          **03 Match & Receive** (Agent — automatic)
          The agent searches your ERP for the matching PO, reconciles quantities, and creates the receiving transaction — all without human involvement.

          **04 Sync & Confirm** (System — synced to ERP)
          The receipt syncs to your ERP. A push notification confirms completion. Your inventory is updated in minutes, not hours.

      - id: features
        content: |
          ## What ReceivePro Does
          Everything between the dock and the ERP — automated.

          **Mobile-First Capture**
          Full-screen camera viewfinder optimized for warehouse environments. Large touch targets for gloved hands. Flash for low-light docks. Scan box after box without waiting.

          **AI Agent Matching**
          The agent searches across your purchasing systems using a cascading match strategy — PO number, vendor + parts, tracking number. It doesn't just look up; it reasons.

          **Cross-System Record Linking**
          Every receiving record links to the PO, Purchase Request, Receipt Ticket, Transportation Ticket, and Carrier Tracking — all clickable, all in one view.

          **Inspection Notices**
          When a received item requires quality inspection, ReceivePro auto-generates an inspection notice and notifies your QA team. No manual handoff, no missed inspections.

          **Carrier Tracking Enrichment**
          The agent queries UPS, FedEx, USPS, and DHL using the extracted tracking number. Delivery confirmation, package count, signed-by name, and proof of delivery — pulled automatically.

          **Multi-Scan Queue**
          Dock workers scan multiple shipments in rapid succession. Each scan enters the pipeline independently. The camera comes right back — no waiting on processing screens.

      - id: expected-receipts
        content: |
          ## Expected Receipts
          Know what's coming before it arrives. Match it automatically when it does.

          Enter tracking numbers ahead of time. Get instant matches at the dock.
          When your inventory specialist gets a shipping notification — an email from the vendor, a carrier confirmation, an ASN — they enter the tracking number into ReceivePro. Optionally link it to a PO. When the shipment arrives and the dock worker scans it, the system already knows what's inside.

          The result: faster receiving, fewer "no match" errors, and complete visibility into what's en route.

      - id: integrations
        content: |
          ## Integrations
          Works with your ERP — whatever it is.

          ReceivePro is built as a companion to Thruline, but it connects to any system of record. You don't need to switch ERPs to use it.

          Best with Thruline. Works with everyone else: Epicor Kinetic, NetSuite, SAP Business One, Infor CloudSuite, Acumatica, SYSPRO, Dynamics 365.

      - id: roi
        content: |
          ## The Numbers
          Manual receiving is costing you more than you think.

          At 25 shipments a day and 10 minutes saved per shipment, ReceivePro pays for itself in the first week.
          - 10s Per shipment (was 8–15 min)
          - <15m Dock-to-stock (was 2–6 hours)
          - 98% 3-way match rate (was 78–88%)
          - <0.5% Error rate (was 3–8%)

      - id: pricing
        content: |
          ## Pricing
          Simple pricing that scales with your dock.

          Every plan includes the full AI agent pipeline, carrier tracking enrichment, Expected Receipts, and inspection notices. No feature gating.

          **Starter — $499/month**
          Single Location. Includes 450 scans/month ($1.50 per additional scan).
          - 1 location, up to 5 users
          - Full AI extraction + auto-receive
          - 1 ERP integration (Thruline, NetSuite, Epicor, or other)
          - Carrier tracking enrichment, Expected Receipts, quality inspection notices
          - Email + chat support

          **Professional — $999/month (Most Popular)**
          Multi-Location. Unlimited scans included.
          - Up to 5 locations, up to 25 users
          - Up to 3 ERP / system integrations
          - Expected Receipts + bulk CSV import
          - Multi-system PO matching (ERP + procurement + TMS)
          - Linked records (PO, PR, receipt ticket, transport ticket)
          - Priority support + onboarding

          **Enterprise — Custom**
          For complex operations with advanced requirements.
          - Unlimited locations, users, and scans
          - Unlimited integrations + custom connectors
          - Expected Receipts + ASN auto-import
          - Custom approval workflows
          - Dedicated onboarding + SLA
          - SSO / SAML + audit logging

          The math is simple: at 25 shipments/day and 10 minutes saved per shipment, ReceivePro saves ~4 hours of dock labor daily. The Starter plan pays for itself in the first week.

      - id: cta
        content: |
          ## Get something built today

          See ReceivePro with your own PO data. We'll set up a pilot in days, not months.

  - path: /#/industries/manufacturing
    title: "Manufacturing | Thruline"
    description: "From job shops to high-volume production, Thruline gives manufacturing teams the tools to plan, execute, and improve — without fighting their software."
    sections:
      - id: hero
        content: |
          # ERP built for how manufacturing actually works

          From job shops to high-volume production, Thruline gives manufacturing teams the tools to plan, execute, and improve — without fighting their software.

      - id: features
        content: |
          ## Features

          **Multi-Level BOM Management**
          Manage complex, multi-level bills of materials with revision control, effectivity dates, and engineering change orders. Support make-to-order, make-to-stock, and configure-to-order.

          **Production Planning & Scheduling**
          Plan and schedule production across work centers with finite capacity constraints. Balance load, minimize changeovers, and meet delivery commitments.

          **Shop Floor Execution**
          Track work orders through every operation in real time. Capture labor, material consumption, and output with barcode scanning and touchscreen terminals.

          **Quality & Compliance**
          Built-in quality management with inspection plans, NCR tracking, CAPA workflows, and SPC. Maintain compliance with ISO, AS9100, IATF, and customer-specific requirements.

          **Inventory & Material Control**
          Real-time inventory visibility with lot traceability, cycle counting, and automated reorder points. Know what you have, where it is, and when to buy more.

          **Tooling & Maintenance**
          Track tool life, schedule preventive maintenance, and manage spare parts inventory. Reduce unplanned downtime and extend equipment life.

          **Supply Chain Coordination**
          Manage vendors, purchase orders, and inbound logistics. Track supplier performance and ensure materials arrive when production needs them.

          **OEE & Performance Analytics**
          Monitor Overall Equipment Effectiveness, throughput, yield, and scrap in real time. Identify bottlenecks and drive continuous improvement with data.

          **Traceability & Recall Readiness**
          Full forward and backward traceability from raw material to finished product. Be audit-ready and recall-capable at all times.

          **Labor & Workforce Management**
          Track direct labor by work order and operation. Manage certifications, training records, and crew assignments across shifts.

          **Machine & IoT Integration**
          Connect to CNC machines, PLCs, and sensors for automated data collection. Eliminate manual data entry and get real-time process visibility.

          **Continuous Improvement**
          Built-in tools for tracking improvement initiatives, measuring results, and sustaining gains. Support lean, Six Sigma, and kaizen methodologies.

  - path: /#/industries/industrial-operations
    title: "Industrial Operations | Thruline"
    description: "Whether you're managing field operations, facility maintenance, or multi-site logistics, Thruline builds the operational backbone your team needs to stay coordinated and compliant."
    sections:
      - id: hero
        content: |
          # Operational software for complex industrial environments

          Whether you're managing field operations, facility maintenance, or multi-site logistics, Thruline builds the operational backbone your team needs to stay coordinated and compliant.

      - id: features
        content: |
          ## Features

          **Work Order Management**
          Create, assign, and track work orders for maintenance, installation, and service operations. Manage priorities, dependencies, and resource allocation.

          **Asset & Site Management**
          Track equipment, facilities, and infrastructure across multiple sites. Maintain asset hierarchies, specifications, and complete maintenance history.

          **Preventive Maintenance**
          Schedule PM tasks based on time, usage, or condition. Automate work order generation and ensure critical equipment stays operational.

          **Resource Scheduling**
          Schedule crews, equipment, and vehicles across jobs and locations. Optimize utilization and avoid conflicts with visual scheduling tools.

          **Safety & Compliance**
          Track safety incidents, manage permits, and maintain compliance documentation. Ensure your operations meet regulatory requirements at every site.

          **Fleet & Logistics**
          Manage vehicle fleets, track deliveries, and coordinate logistics across your operation. Monitor fuel, maintenance, and utilization.

          **Contractor Management**
          Onboard, credential, and manage contractors and subcontractors. Track certifications, insurance, and performance across projects.

          **Project & Job Costing**
          Track costs by project, job, or work order. Capture labor, materials, equipment, and overhead for accurate profitability analysis.

          **Incident Management**
          Report, investigate, and resolve operational incidents. Track root causes, corrective actions, and trends to prevent recurrence.

          **Operational Analytics**
          Monitor KPIs across sites, teams, and equipment. Dashboards for uptime, response times, cost per unit, and operational efficiency.

          **Process Automation**
          Automate routine operational workflows — from approval chains to status notifications to report generation. Reduce manual overhead.

          **Real-Time Monitoring**
          Connect to sensors and control systems for real-time operational visibility. Set thresholds, trigger alerts, and respond to conditions as they happen.

  - path: /#/industries/consumer-products
    title: "Complex Consumer Products | Thruline"
    description: "Consumer products companies deal with variants, regulations, and multi-channel complexity. Thruline builds the operational system that keeps it all connected."
    sections:
      - id: hero
        content: |
          # From formulation to fulfillment — one system

          Consumer products companies deal with variants, regulations, and multi-channel complexity. Thruline builds the operational system that keeps it all connected.

      - id: features
        content: |
          ## Features

          **Product Configuration**
          Manage complex product variants with attributes like size, color, material, and finish. Define BOMs and routings per configuration without duplicating master data.

          **Kitting & Assembly**
          Build kits and assembled products from component inventory. Support both pre-built and assemble-to-order workflows with real-time component availability.

          **Lot Traceability & Compliance**
          Full lot and batch traceability from raw material to finished product. Meet regulatory requirements for consumer safety, labeling, and recall readiness.

          **Order Fulfillment**
          Manage the pick, pack, and ship process with barcode scanning and carrier integration. Support B2B, B2C, and marketplace fulfillment from a single system.

          **E-Commerce Integration**
          Connect to Shopify, Amazon, and other sales channels. Sync orders, inventory, and product data in real time to prevent overselling and delays.

          **Demand Planning**
          Forecast demand based on historical sales, seasonality, and promotions. Align production and purchasing plans with expected demand to avoid stockouts.

          **Returns & RMA Processing**
          Manage customer returns with reason tracking, inspection workflows, and disposition decisions. Process refunds, replacements, or restocking efficiently.

          **Quality & Testing**
          Define quality specifications and testing protocols for consumer products. Track test results, manage holds, and ensure only conforming product ships.

          **Packaging & Labeling**
          Manage packaging BOMs, label templates, and regulatory markings. Support multiple packaging configurations for different channels and markets.

          **Regulatory Compliance**
          Track certifications, testing requirements, and regulatory submissions. Maintain documentation for FDA, CPSC, EU, and other regulatory bodies.

          **Vendor & Co-Manufacturer Management**
          Coordinate with contract manufacturers and suppliers. Share specifications, track production status, and manage quality across your supply network.

          **Multi-Channel Inventory**
          Allocate and manage inventory across retail, wholesale, DTC, and marketplace channels. Prevent overselling with real-time availability across all channels.

  - path: /#/industries/biotech
    title: "Biotech | Thruline"
    description: "From lab bench to commercial manufacturing, Thruline connects your LIMS, ELN, and operational data into a single system — so your team can focus on science, not spreadsheets."
    sections:
      - id: hero
        content: |
          # ERP purpose-built for biotech operations

          From lab bench to commercial manufacturing, Thruline connects your LIMS, ELN, and operational data into a single system — so your team can focus on science, not spreadsheets.

      - id: features
        content: |
          ## Features

          **LIMS & ELN Integration**
          Connect directly to your Laboratory Information Management System and Electronic Lab Notebooks. Sync sample data, assay results, and experimental records into your ERP without manual re-entry or CSV exports.

          **Operational Tracking for R&D and Production**
          Track every stage of your pipeline — from early-stage research through process development to commercial manufacturing. Monitor batch progress, resource utilization, and milestone completion in real time.

          **GxP & FDA Compliance**
          Built-in support for GMP, GLP, and GDP requirements. Maintain 21 CFR Part 11 compliant electronic records with audit trails, electronic signatures, and role-based access controls.

          **Batch Records & Device History**
          Generate and manage electronic batch records and device history records automatically. Capture every material, parameter, and operator action with full traceability from raw material to finished product.

          **Formulation & Recipe Management**
          Manage complex formulations, media recipes, and buffer preparations with version-controlled bills of materials. Track potency, concentration, and expiration across every component.

          **Environmental & Cold Chain Monitoring**
          Integrate with environmental monitoring systems to track temperature, humidity, and particulate counts. Maintain cold chain integrity for biologics, reagents, and temperature-sensitive materials.

          **Lot Traceability & Expiry Management**
          Full lot-level traceability with expiration date tracking, FEFO (First Expired, First Out) enforcement, and certificate of analysis management. Be recall-ready at all times.

          **Document Control & SOPs**
          Centralized document management for SOPs, protocols, validation documents, and regulatory submissions. Version control, review workflows, and training acknowledgment built in.

          **Instrument & Equipment Qualification**
          Track instrument calibration schedules, qualification status (IQ/OQ/PQ), and maintenance records. Ensure every piece of equipment is validated and audit-ready.

          **Process Analytics & Yield Optimization**
          Analyze batch yields, cycle times, and deviation trends across production runs. Identify process improvements with data from both your ERP and integrated lab systems.

          **Training & Competency Management**
          Track operator training records, certifications, and competency assessments. Ensure only qualified personnel execute critical processes — with automatic enforcement.

          **CAPA & Deviation Management**
          Structured workflows for deviations, investigations, corrective actions, and preventive actions. Link CAPAs to batch records, equipment, and quality events for full root cause traceability.

  - path: /#/about
    title: "About | Thruline"
    description: "Our team's experience across manufacturing verticals gives us the insight to know and understand what makes your organization unique."
    sections:
      - id: hero
        content: |
          # The amazing team behind our company

          Our team's experience across manufacturing verticals gives us the insight to know and understand what makes your organization unique.

      - id: team
        content: |
          ## Leadership Team

          **Alex Lazich** — Founder & CEO

          **Jesse Williams** — Founder & CTO

          **Ben Folks** — Founder & Engineer

          **Emily Nucci** — Implementations

      - id: mission
        content: |
          ## Our Mission

          Building the first ERP Factory for the physical world

  - path: /#/about/careers
    title: "Careers | Thruline"
    description: "We're always looking for new talent to help us build the operating system for the physical world."
    sections:
      - id: hero
        content: |
          # Build the future of manufacturing

          We're always looking for new talent to help us build the operating system for the physical world. If you're passionate about solving hard problems in manufacturing and software, we want to hear from you.

          Reach out with your resume and tell us what you're building.

          Email us: careers@thruline.ai

  - path: /#/partners
    title: "Partners | Thruline"
    description: "We work with industry consultants, system integrators, and technology providers to deliver comprehensive solutions to manufacturers."
    sections:
      - id: hero
        content: |
          # Partner with Thruline

          We work with industry consultants, system integrators, and technology providers to deliver comprehensive solutions to manufacturers. As a Thruline partner, you'll get access to our platform, training resources, and dedicated support to help your clients succeed.

          Whether you're looking to implement Thruline for your clients or integrate your technology with our platform, we'd love to explore how we can work together.

          Get in touch: partners@thruline.ai

  - path: /#/resources/thesis
    title: "Our Thesis | Thruline"
    description: "The ERP Factory: The Next Era of Enterprise Software Won't Be Implemented — It Will Be Manufactured"
    sections:
      - id: hero
        content: |
          # The ERP Factory: The Next Era of Enterprise Software Won't Be Implemented — It Will Be Manufactured

          February 2026

      - id: introduction
        content: |
          Something is happening in software development that most of the enterprise world hasn't noticed yet.

          A small team at **StrongDM** just published a manifesto about what they call the Software Factory. Three engineers. No hand-coded software. No human code review. They feed specifications and scenarios into a system of agents that write code, validate it against behavioral clones of real-world services, and iterate until the output converges. Humans define intent. Machines execute.

          They're not alone. Across software companies teams are arriving at the same conclusion from different starting points. The factory model — where humans specify and machines produce — is becoming consensus among people building software at the agentic AI frontier.

          But there's a massive category of enterprise software that this factory revolution hasn't touched. A category where every single deployment is still a bespoke, handcrafted, months-long artisan project.

          **ERP.**

          Enterprise resource planning — the operational backbone of every physical business on the planet — is still stuck in pre-industrial mode. The ERP Factory is the thesis that this era is ending. At Thruline, it's what we're building.

      - id: requirements
        content: |
          ## What an ERP Factory Requires

          At Thruline we believe the following has to be true to construct an ERP Factory:

          **Machine-readable requirements from primary sources.**

          The factory can't start with a whiteboard. A company that's been running its operations for years has already produced an accurate requirements document — it just doesn't look like one. It looks like emails to vendors, purchase orders in spreadsheets, inspection checklists as PDFs, shipping notifications in inboxes, and SOPs buried in shared drives.

          **A traceable link between requirements and configuration.**

          In a factory, every output traces back to its specification. If you can't explain why a configuration exists by pointing to the requirement that created it, you don't have a factory.

          **Expert review as quality control, not as the production process.**

          The ERP factory doesn't eliminate human expertise, it repositions it. Consultants should be diagnosing and correcting, not spending weeks discovering and documenting information that already exists in the client's own files.

          **Automated documentation and knowledge propagation.**

          A factory doesn't just produce a product. It produces the documentation, training materials, and traceability records that make the product maintainable.

          **A feedback loop that compounds.**

          Every unit the factory produces should make the next one better. Implementation knowledge must accumulate in the system, not in individual consultants' heads.

      - id: conclusion
        content: |
          ## Where This Goes

          The ERP industry has been handcrafting one-off systems for forty years. The ERP Factory era starts now.

          Your business already knows how it works. The evidence is in every email, every spreadsheet, every PDF your team produces in the course of doing their jobs. The problem has never been that the knowledge doesn't exist. The problem is that no one has been reading it.

          The ERP Factory reads it. And then it builds you a system that reads it back to you — configured, traced, documented, and ready to evolve as your business does.

          **At Thruline, this is what we're building. Not a better ERP. The first ERP factory.**

      - id: cta
        content: |
          ## Ready to build your ERP factory?

          If you're running operations in a physical industry and your ERP implementation experience has been anything like what I've described — the months of discovery, the documentation that goes stale, the system no one fully understands — we'd like to talk.

  - path: /#/resources/case-studies
    title: "Case Studies | Thruline"
    description: "See how manufacturing and operations teams are using Thruline to replace legacy systems and build software that actually works for them."
    sections:
      - id: hero
        content: |
          # Customer success stories

          See how manufacturing and operations teams are using Thruline to replace legacy systems and build software that actually works for them.

      - id: case-studies
        content: |
          ## Featured Case Studies

          **Hextronics — Manufacturing**
          How Hextronics replaced manual production tracking with real-time visibility
          - Single source of truth
          - Full inventory tracking
          - Iterative system build

          **XBow Systems — Defense Manufacturing**
          Building a GovCloud-ready ERP for defense manufacturing
          - GovCloud compliant
          - Full traceability
          - Multi-site production

          **Fredsense — Biotech**
          Building a custom lab testing and production suite for biotech R&D
          - Faster invoice tracking
          - Unified customer reports
          - Custom white-labeled ERP

  - path: /#/resources/case-studies/hextronics
    title: "Hextronics Case Study | Thruline"
    description: "How Hextronics replaced manual production tracking with real-time visibility"
    sections:
      - id: hero
        content: |
          # How Hextronics replaced manual production tracking with real-time visibility

          **Company:** Hextronics
          **Industry:** Manufacturing
          **Key Results:** Single source of truth, Full inventory tracking, Iterative system build

      - id: challenge
        content: |
          ## The Challenge

          Hextronics manufactures autonomous drone docking stations — complex electromechanical assemblies with hundreds of components and strict quality requirements. Their production team was tracking everything in spreadsheets and whiteboards.

          They tried both Cin7 and Microsoft Dynamics to track their manufacturing inventory, but both systems were unable to be used in any way by the Hextronics team. The complexity and rigidity of these legacy systems made them impractical for their fast-moving drone manufacturing operation.

          They needed a production system that could keep up with their engineering pace and actually work for their team — not a legacy system that would force them to change how they work.

      - id: solution
        content: |
          ## The Solution

          Hextronics started with purchasing, then upgraded to inventory management, and now handle manufacturing as well as accounting. Hextronics has migrated off of Cin7, QuickBooks, and HubSpot into a single unified system.

          Thruline then expanded adjusted their purchasing workflows, inventory tracking, and manufacturing processes to get them using comprehensive inventory management. They now track all of their production runs, bills of materials, and components within Thruline.

          We also implemented sales orders and quoting functionality, giving them end-to-end visibility from quote to cash. Every configuration was built iteratively based on their actual workflows and requirements.

      - id: outcome
        content: |
          ## The Outcome

          Thruline were able to iteratively build a system specifically for Hextronics. They can now do inventory management, manufacturing tracking, and purchase requisition with their small team, all tracked within Thruline.

          Hextronics now has a single source of truth for inventory, sales, quoting, as well as manufacturing. No more jumping between multiple systems or reconciling data across platforms.

          The system grows with their business — as their needs evolve, Thruline continue to build new capabilities that match their exact requirements.

      - id: quote
        content: |
          "Thruline Rocks!" — Shaun, Operations Manager

  - path: /#/resources/case-studies/xbow-systems
    title: "XBow Systems Case Study | Thruline"
    description: "Building a GovCloud-ready ERP for defense manufacturing"
    sections:
      - id: hero
        content: |
          # Building a GovCloud-ready ERP for defense manufacturing

          **Company:** XBow Systems
          **Industry:** Defense Manufacturing
          **Key Results:** GovCloud compliant, Full traceability, Multi-site production

      - id: challenge
        content: |
          ## The Challenge

          XBow Systems manufactures solid rocket motors for defense and space applications. As a government contractor, they face strict compliance requirements including ITAR and the need for GovCloud-hosted infrastructure.

          Their existing tools — a mix of spreadsheets, shared drives, and a basic inventory system — couldn't provide the traceability, access controls or inventory tracking required for their complex formulations and manufacturing.

          They evaluated traditional defense ERP systems but found them prohibitively expensive, slow to implement, and designed for companies ten times their size.

      - id: solution
        content: |
          ## The Solution

          XBow started with Procurement and Inventory, building the foundation for their operations. We've now expanded to include integrations with SAM vendor watch list functionality for compliance tracking.

          We built out production planning as well as manufacturing across three different sites. This includes custom approval flows for purchasing and the RFQ process, ensuring proper oversight and compliance at every step.

          We also implemented custom formulation-based manufacturing tracking for their R&D and full manufacturing teams, handling the unique requirements of energetic material processing and propellant batch tracking.

      - id: outcome
        content: |
          ## The Outcome

          XBow now has a fully compliant ERP system that satisfies government requirements and keeps their engineering team productive across all their manufacturing sites.

          Full forward and backward traceability is maintained from raw material certificates through final acceptance testing. Every configuration in the system is linked to a documented requirement.

          The system runs on GovCloud with the security controls their contracts require, without the million-dollar price tag of traditional defense ERP systems.

      - id: quote
        content: |
          "Thruline helps us operate in startup mode by giving us a system that isnt overly complex" — VP of Operations, XBow Systems

  - path: /#/resources/case-studies/fredsense
    title: "Fredsense Case Study | Thruline"
    description: "Building a custom lab testing and production suite for biotech R&D"
    sections:
      - id: hero
        content: |
          # Building a custom lab testing and production suite for biotech R&D

          **Company:** Fredsense
          **Industry:** Biotech
          **Key Results:** Faster invoice tracking, Unified customer reports, Custom white-labeled ERP

      - id: challenge
        content: |
          ## The Challenge

          Fredsense needed a completely custom lab testing, tracking, and manufacturing production suite, as well as an invoice management tool for their biotech R&D lab testing and production kits.

          Their spreadsheet-based system for managing tests was becoming unmanageable as they scaled. Test data, sample tracking, and customer reporting were scattered across multiple files with no unified view.

          They needed a system that could handle their unique biotech workflows — from contract management to sample testing to production kits — without forcing them into a generic lab information management system.

      - id: solution
        content: |
          ## The Solution

          Fredsense has a completely custom white-labeled ERP system that tracks everything from their contracts to sample tests, sample orders, as well as inventory. The system was built specifically for their biotech workflows.

          Thruline was able to construct this within the 60-day timeline that meets all of Fredsense's custom requirements. The system handles their unique testing protocols, sample tracking, and production workflows.

          We built custom modules for lab test management, sample order processing, and inventory tracking — all integrated into a single platform that replaces their spreadsheet-based approach.

      - id: outcome
        content: |
          ## The Outcome

          Invoice tracking and analysis is now significantly faster for Fredsense. What used to take hours of manual work is now automated and accurate.

          They now have all reports for their customers in one place, making it easy to generate compliance documentation and test results. Customer-facing reports are generated automatically from test data.

          We are now looking to expand the system to better track growing inventory with growing sales, as well as installing our sales and quoting functionality to support their continued growth.

      - id: quote
        content: |
          "Thruline built us a completely custom system that handles our unique biotech workflows. Everything from sample tracking to customer reports is now in one place." — Operations Manager, Fredsense

  - path: /#/resources/blog
    title: "Blog | Thruline"
    description: "Thoughts on manufacturing, ERP, and building software for the physical world."
    sections:
      - id: hero
        content: |
          # Insights & Updates

          Thoughts on manufacturing, ERP, and building software for the physical world.

      - id: posts
        content: |
          ## Blog Posts

          **We Built a Custom ERP at a Rocket Factory. Here's What We Learned.**
          Date: March 9, 2026 | Category: Company | Read Time: 18 min
          How Astra Space built AstraOS — inspired by SpaceX's Warp Drive — and why we're bringing that playbook into the AI era at Thruline.

          **The ERP Factory Thesis: Why We Built Thruline**
          Date: February 15, 2026 | Category: Company | Read Time: 8 min
          The traditional ERP implementation model is broken. Discovery takes months, costs spiral, and the result is software that doesn't match how your team actually works. We built Thruline to fix that — here's the thesis behind the company.

          **Connecting Engineering and ERP Data for Real-World Analytics**
          Date: December 2, 2025 | Category: Case Study | Read Time: 6 min
          When you're dealing with complex engineering projects, having data scattered across different systems isn't just inconvenient — it's a roadblock to making informed decisions. See how one engineer used Thruline's API to bridge the gap.

          **What it Takes to Do a 60-Day ERP Implementation**
          Date: December 2, 2025 | Category: ERP | Read Time: 8 min
          Remember when getting an ERP system up and running felt like planning a moon landing? The 60-day implementation timeline represents a fundamental shift in how smart companies approach enterprise software.

          **7 Signs Your Manufacturing Business Needs Better Inventory Management Software**
          Date: October 4, 2025 | Category: Inventory | Read Time: 9 min
          How do you know when your inventory management has crossed the line from "quirky" to "completely dysfunctional"? Here are seven telltale signs it's time to ditch the spreadsheets.

          **Custom ERP vs Pre-Configured Solutions: Which Is Better For Your Manufacturing Business?**
          Date: September 29, 2025 | Category: ERP | Read Time: 10 min
          The choice between a custom ERP solution and a pre-configured system isn't just about software: it's about aligning your technology infrastructure with your manufacturing processes, growth plans, and competitive strategy.

          **7 Mistakes You're Making with ERP Integrations (and How to Fix Them)**
          Date: September 29, 2025 | Category: ERP | Read Time: 8 min
          ERP integrations can make or break your manufacturing operations. After working with countless manufacturing companies, we've seen the same integration mistakes repeated over and over.

          **Manufacture Your Way, Not Theirs**
          Date: July 8, 2025 | Category: Manufacturing | Read Time: 4 min
          Make sure your solutions are customized to match your company's processes. Changing your process to match a software system takes away one of the greatest competitive edges your company has.

          **How Companies Handle Engineer Purchase Requests: Three Approaches to Procurement**
          Date: May 4, 2025 | Category: ERP | Read Time: 5 min
          In the fast-paced world of engineering, efficient procurement processes can make or break project timelines. Let's explore the three main organizational structures and how accounting teams manage these transactions.

          **How Manufacturing Methodologies Shape ERP System Needs**
          Date: October 15, 2024 | Category: ERP | Read Time: 5 min
          The way a manufacturer operates impacts the effectiveness of its ERP system. While two companies might produce similar products, the processes behind their operations can vary significantly.

          **ERP is Dead - Long Live ERP**
          Date: October 15, 2024 | Category: ERP | Read Time: 4 min
          There's been a theory that ERP is going the way of the dinosaur, to be replaced by a frankenstein of SaaS solutions. With recent massive fundings, this theory doesn't seem to be panning out.

          **Inventory Management: What do I have?**
          Date: October 15, 2024 | Category: Inventory | Read Time: 6 min
          The first question that an inventory management system has to answer is: What inventory do I have? Here's how to track parts, locations, and quantities effectively.

          **Inventory Management: Forecasting**
          Date: October 15, 2024 | Category: Inventory | Read Time: 4 min
          Once you know what parts you're tracking and where they are, what do you do with that information? First you need to understand what you're using — and then plan what to buy next.

          **Project Costing - Why is it so difficult**
          Date: October 15, 2024 | Category: Manufacturing | Read Time: 4 min
          Determining the entire cost of a project is extremely difficult. You need to track tasks, expenses, time, and contracts — and integrate that information together.

          **What does customizable software actually mean?**
          Date: October 15, 2024 | Category: ERP | Read Time: 4 min
          Every software company offers some level of customization. But how does Thruline stand out? We think about customization like the levels of self-driving cars.

          **Thruline Announcement**
          Date: October 15, 2024 | Category: Company | Read Time: 2 min
          At Thruline we're building an ERP system that every small business can (actually) afford, with an implementation timeframe measured in days, not months.

  - path: /#/resources/demo-videos
    title: "Demo Videos | Thruline"
    description: "Watch quick demos of Thruline's core capabilities — from production tracking to quality management."
    sections:
      - id: hero
        content: |
          # See Thruline in action

          Watch quick demos of Thruline's core capabilities — from production tracking to quality management.

          Note: This is a subset of our functionality. Contact us for the full set.

      - id: videos
        content: |
          ## Demo Videos

          **Purchase Orders and Receiving**
          Create purchase orders, track vendor deliveries, and process receiving with quality inspections and inventory updates.

          **Deal Initiation and Project Creation**
          Initiate new deals, create project records, and set up the foundational structure for project management.

          **Project Planning**
          Plan project timelines, allocate resources, define milestones, and establish deliverables for successful execution.

  - path: /#/resources/integrations
    title: "Integrations | Thruline"
    description: "Thruline connects with the tools your team already uses. Pre-built integrations and custom webhooks for seamless data flow."
    sections:
      - id: hero
        content: |
          # Integrations

          Thruline connects with the tools your team already uses. Pre-built integrations and custom webhooks for seamless data flow.

      - id: integrations
        content: |
          ## Available Integrations

          **Accounting & Finance**
          QuickBooks, Xero, NetSuite

          **E-commerce & Sales**
          Shopify, WooCommerce, Stripe

          **Communication**
          Slack, Microsoft Teams, Email

          **Custom Integrations**
          REST API, Webhooks, SSO/SAML

  - path: /#/resources/changelog
    title: "Changelog | Thruline"
    description: "A curated summary of what we've shipped — focused on the changes that matter most to you. January 2025 through February 2026."
    sections:
      - id: hero
        content: |
          # Changelog

          A curated monthly summary of notable changes to the Thruline Platform, focused on customer-facing features and improvements.

      - id: highlights
        content: |
          ## Recent Highlights

          **February 2026**
          - Custom quotes UI with configurable layouts
          - CRM module overhauled with new account, deal, and contact configurations
          - Duplicate serial number detection across inventory
          - Concurrent record update conflict detection

          **January 2026**
          - AI knowledge base system for smarter prompts
          - Vendor watch list module with Trade.gov compliance screening
          - Serial number uniqueness enforcement
          - Role-based form conditions

          **December 2025**
          - AI system launched on AWS Bedrock with OpenSearch vector store
          - As-built module tree for assembly genealogy tracking
          - BOM operation cost tracking
          - Workflow tree conditions and user input prompts

  - path: /#/resources/reference-guides
    title: "Reference Guides | Thruline"
    description: "Vendor-neutral reference guides on how ERP, MES, WMS, inventory, and procurement systems divide the work — what each layer owns and where the hand-offs break."
    sections:
      - id: hero
        content: |
          # Understand the systems that run your operation

          Vendor-neutral reference packets on how ERP, MES, WMS, inventory, and procurement systems actually divide the work — what each layer owns, where the hand-offs break, and how to tell which system you need.

      - id: guides
        content: |
          ## Available guides

          - **Inventory Management Systems** (12 min) — "Do you actually need a WMS, or is ERP inventory enough?" Covers inventory-only tools (Cin7, Fishbowl), ERP inventory (NetSuite, SAP), and full WMS platforms (Manhattan, Extensiv).
          - **Manufacturing & MES Systems** (10 min) — "Where does your ERP stop and your shop floor begin?" Covers ERP, MES, SOPs, and work order tracking.
          - **Procurement Systems** (11 min) — "Can you prove the last three POs were authorised?" Covers purchase requests, RFQs, approvals, POs, and receiving.

          Each guide includes a capability ownership matrix, a full lifecycle walkthrough, and a downloadable PDF packet.

          In progress: Quality & QMS, Sales & Quoting (CPQ), Scheduling & Capacity Planning, Costing & the GL.

  - path: /#/resources/reference-guides/inventory-management-systems
    title: "Inventory Management Systems | Reference Guide | Thruline"
    description: "How standalone inventory tools, ERP inventory modules, and a full WMS split the inventory lifecycle — and which one your complexity level actually calls for."
    sections:
      - id: overview
        content: |
          # Inventory Management Systems

          Three tiers handle inventory and they are not interchangeable. Inventory-only systems (Cin7, Fishbowl, inFlow) track what you have and where it is — fast to set up, but shallow financial integration, no demand planning, no warehouse execution. ERP inventory (NetSuite, SAP, Dynamics 365) connects stock to the general ledger, MRP, and available-to-promise. A WMS (Manhattan, Blue Yonder, Extensiv) orchestrates physical movement — directed putaway, optimised pick paths, real-time operator tasks.

          The guide covers the four-stage inventory lifecycle (receipt, storage and replenishment, picking and fulfillment, visibility and reconciliation), a capability ownership matrix across all three tiers, and where Thruline fits.

      - id: thruline-fit
        content: |
          ## Where Thruline fits

          Three ways: replacing spreadsheets or an outgrown inventory-only tool with inventory plus manufacturing execution; layering onto an existing ERP as the execution layer that keeps inventory data accurate; or providing lightweight WMS functionality (barcode scanning, bin tracking, directed picks) without a full WMS implementation.

  - path: /#/resources/reference-guides/manufacturing-execution-systems
    title: "Manufacturing & MES Systems | Reference Guide | Thruline"
    description: "ERP, MES, SOPs, and work order tracking — the four layers that turn a plan into a finished good, and the broken hand-offs that show up in almost every plant."
    sections:
      - id: overview
        content: |
          # Manufacturing & MES Systems

          Four layers share responsibility for turning a plan into a finished product. ERP is the business brain (planning, purchasing, inventory, scheduling). MES is the shop-floor brain (dispatching work orders, guiding operators, recording what was built). SOPs are the institutional knowledge layer. Work order tracking is the connective tissue.

          ERP ends where the shop floor begins: it creates the work order but cannot see what happens during production. MES fills that gap, but only if the two are integrated.

          The guide covers the nine-phase manufacturing lifecycle, a full ERP vs MES comparison, why SOPs fail without digital enforcement, and a capability ownership matrix across all four layers.

      - id: thruline-fit
        content: |
          ## Where Thruline fits

          Two ways: as the integration and execution layer between an existing ERP and MES, holding SOPs and work orders as the single source of truth; or as the full execution layer for manufacturers with no MES, replacing printed travelers, binder SOPs, and whiteboard tracking.

  - path: /#/resources/reference-guides/procurement-systems
    title: "Procurement Systems | Reference Guide | Thruline"
    description: "How purchase requests, RFQs, approvals, POs, and receiving run the buy-side lifecycle — what each document owns and where the hand-offs break."
    sections:
      - id: overview
        content: |
          # Procurement Systems

          Five documents run the buy-side lifecycle. Purchase Requests are the need layer (internal ask, non-binding). RFQs are the sourcing layer (multiple suppliers, identical spec). Approvals are the governance layer (spend authority routed by amount, category, policy). Purchase Orders are the commitment layer (legally binding once acknowledged). Receiving is the close layer (3-way match, inventory post-back).

          The core gap: PRs live in email or spreadsheets, POs live in the ERP, and there is no clean bridge between them. That is how maverick spend, duplicate orders, and missed lead times get baked into the process.

          The guide covers PR vs PO in full, RFQs and approval workflows, the twelve-step procure-to-pay lifecycle, and a capability ownership matrix across all five documents.

      - id: thruline-fit
        content: |
          ## Where Thruline fits

          Two ways: as the request, sourcing, and approval layer in front of an existing ERP, feeding it clean approved POs and closing the receiving loop; or as the full procurement layer for teams running on PR spreadsheets, quote emails, and verbal approvals.

  - path: /#/resources/terms
    title: "Terms & Privacy | Thruline"
    description: "End User License Agreement and Privacy Policy for Thruline software and services."
    sections:
      - id: hero
        content: |
          # Terms & Privacy

          Last Updated: September 3, 2024

      - id: eula
        content: |
          ## End User License Agreement (EULA)

          This End User License Agreement ("Agreement") is a legal agreement between you ("Licensee" or "you") and Redshift Enterprises ("Licensor" or "we") regarding your use of Thruline ("Software").

          **1. License Grant**

          Subject to the terms and conditions of this Agreement, Licensor grants you a non-exclusive, non-transferable, limited license to install and use the Software on a single device for personal or internal business purposes.

          **Restrictions:** You may not modify, reverse engineer, redistribute, or remove proprietary notices from the Software.

          **2. Intellectual Property Rights**

          The Software and all related materials are protected by intellectual property laws. Licensor retains all rights, title, and interest in the Software.

          **3. Limitation of Liability**

          The Software is provided "AS IS" without warranty of any kind. Licensor shall not be liable for any special, incidental, indirect, or consequential damages.

          **4. Termination**

          Licensor may terminate this Agreement if you fail to comply with any term. Upon termination, you must cease all use and destroy all copies of the Software.

          **5. Governing Law**

          This Agreement shall be governed by applicable jurisdiction laws.

      - id: privacy
        content: |
          ## Privacy Policy

          This Privacy Policy applies to your use of our website, subscription services, and software.

          **1. Information We Collect**

          - Information you give us (name, email, payment details)
          - Information from your use of Services (device info, log data, location, cookies)

          **2. How We Use Information**

          - Provide and improve the Service
          - Process transactions and send confirmations
          - Customer support and technical notices
          - Product updates and special offers
          - Monitor usage and analyze trends

          **3. Sharing of Personal Information**

          We do not share your personal information except:
          - With your consent
          - With service providers who work on our behalf
          - To comply with legal requirements
          - To protect our rights and safety
          - In connection with business transactions

          **4. Your Choices**

          You may opt out of marketing communications. We may still send service-related messages.

          **5. Security**

          We take reasonable measures to protect your personal information.

          **6. Third Party Links**

          Our Service may contain links to other websites. We are not responsible for their privacy practices.

          **7. Changes to Privacy Policy**

          We may update this Privacy Policy. Continued use constitutes consent to changes.

          **8. Contact**

          Questions? Contact us at contact@thruline.co
`;